/**
 * usePagination Hook
 * Reusable hook for pagination logic with "Show More" functionality
 */

import { useState, useCallback, useMemo } from 'react'

interface UsePaginationOptions<T> {
  items: T[]
  initialLimit?: number
  increment?: number
}

interface UsePaginationReturn<T> {
  displayedItems: T[]
  hasMore: boolean
  showMore: () => void
  reset: () => void
  currentLimit: number
  totalItems: number
}

/**
 * Hook for pagination with "Show More" functionality
 * @param items - Array of items to paginate
 * @param initialLimit - Initial number of items to show (default: 10)
 * @param increment - Number of items to add when "Show More" is clicked (default: 10)
 */
export function usePagination<T>({
  items,
  initialLimit = 10,
  increment = 10,
}: UsePaginationOptions<T>): UsePaginationReturn<T> {
  const [currentLimit, setCurrentLimit] = useState(initialLimit)

  const displayedItems = useMemo(() => {
    return items.slice(0, currentLimit)
  }, [items, currentLimit])

  const hasMore = useMemo(() => {
    return items.length > currentLimit
  }, [items.length, currentLimit])

  const showMore = useCallback(() => {
    setCurrentLimit((prev) => Math.min(prev + increment, items.length))
  }, [increment, items.length])

  const reset = useCallback(() => {
    setCurrentLimit(initialLimit)
  }, [initialLimit])

  return {
    displayedItems,
    hasMore,
    showMore,
    reset,
    currentLimit,
    totalItems: items.length,
  }
}

